import { usePromoCodeTracking } from "components/Basket/Basket.tracking";
import { useExpressCheckoutContext } from "components/Basket/hooks/useExpressCheckoutContext";
import { addPromoCode, removeCtPromoCode } from "lib/api";
import { getUser } from "lib/user";
import { useCallback, useMemo, useState } from "react";
import {
  showPromoCodeEnabled,
  showPromoCodeVariants,
  promoCodeValue
} from "../constants";

export const showPromoCodeEnable = (promoCodeVariant: string) => {
  return showPromoCodeEnabled.includes(promoCodeVariant);
};

export const showPromoCodeAllVariants = (promoCodeVariant: string) => {
  return showPromoCodeVariants.includes(promoCodeVariant);
};

export const usePromoCodeActions = () => {
  const {
    trackPromoCodeEntered,
    trackPromoCodeApplied,
    trackPromoCodeRejected,
    trackPromoCodeRemoved
  } = usePromoCodeTracking();
  const { handleExpressCheckout } = useExpressCheckoutContext();
  const { includedDiscounts, rejectedDiscounts } = getUser();

  const isPromoCodeActivated = useMemo(() => {
    return (
      includedDiscounts.filter(({ code }) => code === promoCodeValue).length > 0
    );
  }, [includedDiscounts]);

  const isPromoCodeRejected = useMemo(() => {
    return (
      rejectedDiscounts.filter(({ code }) => code === promoCodeValue).length > 0
    );
  }, [rejectedDiscounts]);

  const [warning, setWarning] = useState("");
  const applyPromoCode = useCallback(async () => {
    if (isPromoCodeActivated) {
      setWarning("Promo code is already applied");
      return;
    }

    trackPromoCodeEntered(promoCodeValue);

    try {
      await addPromoCode(promoCodeValue, {
        onCodeApplied: ({ name, value }) =>
          trackPromoCodeApplied(promoCodeValue, name, value),
        onCodeRejected: ({ state }) =>
          trackPromoCodeRejected(promoCodeValue, state)
      });

      if (handleExpressCheckout) {
        handleExpressCheckout();
      }
    } finally {
      setWarning("");
    }
  }, [
    isPromoCodeActivated,
    trackPromoCodeEntered,
    trackPromoCodeApplied,
    trackPromoCodeRejected,
    handleExpressCheckout
  ]);

  const removePromoCode = useCallback(async () => {
    await removeCtPromoCode(promoCodeValue);

    if (handleExpressCheckout) {
      handleExpressCheckout();
    }
    trackPromoCodeRemoved(promoCodeValue);
  }, [trackPromoCodeRemoved, handleExpressCheckout]);

  return {
    onUsePromoCode: applyPromoCode,
    onRemovePromoCode: removePromoCode,
    isPromoCodeActivated,
    isPromoCodeRejected,
    warning
  };
};
